const DEFAULT_REDIRECT_PATH = "/";
const FALLBACK_ORIGIN = "https://redirect.local";

/**
 * First path segments that are never a valid post-login destination.
 *
 * All three react-router surfaces mount the same public route set — `login`, `forgot-password`,
 * `activate-account/:changePasswordId` (`web/src/brokerage/router/public-routes.tsx`,
 * `inbox/src/router.tsx`, `panels/src/router.tsx`) — and landing on any of them holding a fresh
 * valid token is a dead end, not just `/login`.
 *
 * Matched as a whole segment rather than by prefix, so a future `/login-history` is not caught.
 */
const PUBLIC_ROUTE_SEGMENTS = new Set(["login", "forgot-password", "activate-account"]);

/**
 * The first path segment, normalized the way the router normalizes it before matching.
 *
 * Two normalizations, both required, both verified against the installed react-router:
 *
 * - **Case.** `compilePath` builds `new RegExp(source, caseSensitive ? undefined : "i")` and routes
 *   default to `caseSensitive: false`, which no public route sets. `matchRoutes(routes, "/LOGIN")`
 *   matches `login`.
 * - **One percent-decode.** `matchRoutes` runs the pathname through `decodePath` first, so
 *   `/%6cogin`, `/%6Cogin` and `/lo%67in` all match `login`. `searchParams.get()` has already
 *   decoded once by the time we are called, so `?redirect=%2F%256cogin` hands us `/%6cogin` — a
 *   raw comparison passes it, and the user lands back on the login form holding a valid token,
 *   which is this ticket's exact symptom coming through the guard meant to prevent it.
 *
 * Exactly one decode, to match the router: it decodes once too, so `/%256cogin` matches nothing and
 * must stay allowed rather than being treated as `login`. On a malformed escape (`%zz`, or a bare
 * `%` in a legitimate path) `decodeURIComponent` throws and `decodePath` warns and keeps the raw
 * value — so falling back to the raw segment is what the router will compare, not a guess.
 */
const firstPathSegment = (pathname: string): string => {
  const raw = pathname.split("/")[1] ?? "";

  try {
    return decodeURIComponent(raw).toLowerCase();
  } catch {
    return raw.toLowerCase();
  }
};

/**
 * Builds the login URL with a redirect parameter pointing to the current page.
 * Works both inside and outside of React (uses window.location directly).
 */
export function getLoginRedirectUrl(): string {
  return `/login?redirect=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`;
}

/**
 * The counterpart read: turns a `?redirect=` parameter back into a path that is safe to navigate to.
 *
 * The parameter is user-controlled and every consumer re-resolves whatever we return —
 * `navigate()` in `AuthSignIn`, `location.replace()` in the two Next.js login forms — so the
 * contract is about the **output**, not the input. Checking only that the input resolved
 * same-origin is not enough: `new URL` collapses `..` segments, and a collapse can leave a leading
 * double slash in `pathname`, which is same-origin as an input and protocol-relative as an output.
 * `/a/..//evil.example/path` resolves to `https://<origin>/…` and returns `//evil.example/path`,
 * which the consumer then resolves to `https://evil.example/path` — an off-site navigation from a
 * link anyone can send.
 *
 * So the returned string is re-resolved and required to still be same-origin. The leading-`//`
 * rejection below is the named instance of that same invariant, not independent coverage — every
 * input either check currently rejects, the other rejects too. It is kept because it says out loud
 * what goes wrong, while the re-resolution is what holds if a future normalization produces
 * something nobody enumerated.
 *
 * Falls back to `FALLBACK_ORIGIN` when there is no `location` (SSR, and a node test environment),
 * where every relative path resolves same-origin and every absolute one does not.
 */
export function getSafeRedirectPath(redirectParam: string | null): string {
  if (!redirectParam) {
    return DEFAULT_REDIRECT_PATH;
  }

  const baseOrigin = globalThis.location?.origin ?? FALLBACK_ORIGIN;

  try {
    const redirectUrl = new URL(redirectParam, baseOrigin);

    if (redirectUrl.origin !== baseOrigin || PUBLIC_ROUTE_SEGMENTS.has(firstPathSegment(redirectUrl.pathname))) {
      return DEFAULT_REDIRECT_PATH;
    }

    const path = `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;

    // Protocol-relative on the way out, whatever it was on the way in.
    if (redirectUrl.pathname.startsWith("//")) {
      return DEFAULT_REDIRECT_PATH;
    }

    // Validate what we are about to hand back, resolved the way the consumer will resolve it.
    if (new URL(path, baseOrigin).origin !== baseOrigin) {
      return DEFAULT_REDIRECT_PATH;
    }

    return path;
  } catch {
    return DEFAULT_REDIRECT_PATH;
  }
}
